/**
 * The export pipeline on the main thread (W2-09 publish-import; DESIGN.md §5.11, §7.3; ARCHITECTURE.md §11):
 *
 *   options (project settings + the user's last export preferences + the session's RPE version override)
 *   -> what will be written (lowered exactly like the exporter: planned files and sizes, conversion report)
 *   -> where it goes (asked first, while the click still counts as a user action: the browser save dialog on
 *      Chromium, remembered folder; a download elsewhere)
 *   -> persist.flush() -> io worker `exportPackage` (lower -> encode -> zip -> STRICT self-check)
 *   -> a failed self-check (an exporter bug) or an empty result (the encoder refused the chart) is reported; a failed
 *      self-check needs confirmation before the file is written, an empty result is never written
 *   -> written -> the project records the export (Core.doc.recordExport, "changed since export") and a version
 *      "Exported" is kept (persist.checkpoint kind 'export').
 *
 * "Copy chart.json" is the same chart-only export put on the clipboard; its clipboard write is opened during the
 * click (`openClipboard`) and receives the text once the export is done, so slow exports still copy on browsers that
 * accept a clipboard write only while the click counts as a user action.
 *
 * Browser specifics are behind `Saver` (tests pass a fake one).
 */

import type { AppServices } from '../../app/registry/types';
import { Core } from '../../core/commands/index';
import type { Engine } from '../../core/engine/types';
import { Lower } from '../../core/ir/lower';
import type { IrChart, IrExtraJson, LowerReport } from '../../core/ir/types';
import type { ChartDoc, DocIndexes, ProjectId } from '../../core/model/types';
import { isNumericId } from '../../core/util/ids';
import type { MessageRef } from '../../core/util/types';
import { encodeExtra } from '../../formats/extra/encode';
import { writeInfoTxt } from '../../formats/info/infoTxt';
import { writeInfoYml } from '../../formats/info/infoYml';
import { infoOnlySwitches, planNames, rpeVersionOverrideEffect } from '../../formats/package/exportPackage';
import { infoFromDoc } from '../../formats/package/manifestDoc';
import { Names } from '../../formats/package/names';
import { Sidecar, SIDECAR_FILE } from '../../formats/package/sidecar';
import { encodeRpe } from '../../formats/rpe/encode';
import type { ExportOptions, ExportResult, ExportTarget, PackageFileEntry } from '../../formats/types';
import type { DocStore } from '../../state/types';
import { publishSession, type Destination, type ExportPrefs } from './prefs';
import { m } from './publish.messages';

// ---------------------------------------------------------------------------------------------------------------
// Options and names
// ---------------------------------------------------------------------------------------------------------------

/** Export options from the project's package settings, the user's preferences and the session override. */
export function exportOptionsFor(doc: ChartDoc, prefs: ExportPrefs, rpeVersionOverride: number | null): ExportOptions {
  return {
    target: prefs.target,
    profile: doc.pkg.exportProfile,
    naming: doc.pkg.fileNaming,
    includeInfoTxt: prefs.includeInfoTxt,
    includeSidecar: prefs.includeSidecar,
    includeUnusedAssets: prefs.includeUnusedAssets,
    pretty: prefs.pretty,
    floatStyle: prefs.floatStyle,
    rpeVersionOverride,
    bakeSpeedForChartOnly:
      prefs.target === 'chartJson' && doc.pkg.speedMode === 'rpe170' && prefs.bakeSpeedForChartOnly,
  };
}

/** Options of the project's current export (its last preferences and session override). */
export function currentOptions(doc: ChartDoc, project: ProjectId, prefs: ExportPrefs): ExportOptions {
  return exportOptionsFor(doc, prefs, publishSession.rpeVersionOverride(project));
}

export function extensionOf(target: ExportTarget): 'pez' | 'zip' | 'json' {
  return target === 'chartJson' ? 'json' : target === 'zip' ? 'zip' : 'pez';
}

export function mimeOf(target: ExportTarget): string {
  return target === 'chartJson' ? 'application/json' : 'application/zip';
}

/** "Title [Level].pez" (the RPE id, else the localised "Untitled", stands in for an empty title). */
export function defaultFileName(doc: ChartDoc, target: ExportTarget, untitled: string): string {
  const fallback = isNumericId(doc.meta.rpeId) ? doc.meta.rpeId : untitled;
  return Names.packageFileName(doc.meta.name, doc.meta.level, extensionOf(target), fallback);
}

const FORBIDDEN_IN_NAMES = /[\\/:*?"<>|]/g;

/**
 * A file name typed by the user made safe for downloads: separators and characters file systems refuse become `_`,
 * control characters go, the target's extension is added when missing. Empty input gives `fallback`.
 */
export function cleanFileName(typed: string, target: ExportTarget, fallback: string): string {
  let name = '';
  for (const ch of typed) {
    const code = ch.codePointAt(0) ?? 0;
    if (code >= 0x20 && code !== 0x7f) name += ch;
  }
  name = name.replace(FORBIDDEN_IN_NAMES, '_').replace(/\s+/g, ' ').trim();
  name = name.replace(/^\.+/, '').trim();
  if (name === '') return fallback;
  const ext = `.${extensionOf(target)}`;
  const lower = name.toLowerCase();
  const isPackage = target !== 'chartJson';
  if (lower.endsWith(ext) || (isPackage && (lower.endsWith('.pez') || lower.endsWith('.zip')))) return name;
  return `${name}${ext}`;
}

// ---------------------------------------------------------------------------------------------------------------
// What will be written
// ---------------------------------------------------------------------------------------------------------------

export interface Lowered {
  readonly chart: IrChart;
  readonly extra: IrExtraJson | null;
  readonly report: LowerReport;
}

/** The speed mode the export lowers with (the chart-only speed bake lowers an RPE 1.7 project in Classic mode). */
export function exportSpeedMode(doc: ChartDoc, opts: Pick<ExportOptions, 'target' | 'bakeSpeedForChartOnly'>) {
  return opts.target === 'chartJson' && opts.bakeSpeedForChartOnly && doc.pkg.speedMode === 'rpe170'
    ? 'legacy'
    : doc.pkg.speedMode;
}

/**
 * The IR the export writes (the engine's cached IR when the export lowers with the project's own settings, which is
 * what the preview shows; otherwise lowered here exactly like the exporter), with the RPE version override applied.
 */
export function lowerForExport(
  doc: ChartDoc,
  idx: DocIndexes,
  opts: Pick<
    ExportOptions,
    'target' | 'bakeSpeedForChartOnly' | 'profile' | 'naming' | 'includeUnusedAssets' | 'rpeVersionOverride'
  >,
  engine: Pick<Engine, 'ir' | 'irExtra' | 'lowerReport'> | null,
): Lowered {
  const speedMode = exportSpeedMode(doc, opts);
  let lowered: Lowered;
  if (engine !== null && speedMode === doc.pkg.speedMode && opts.profile === doc.pkg.exportProfile) {
    lowered = { chart: engine.ir(), extra: engine.irExtra(), report: engine.lowerReport() };
  } else {
    const plan = planNames(doc, idx, opts);
    lowered = Lower.lowerChart(doc, idx, {
      profile: opts.profile,
      speedMode,
      speedBakeTolPx: doc.pkg.speedBakeTolPx,
      assetPath: (id) => plan.paths.get(id) ?? doc.assets.get(id)?.path ?? id,
    });
  }
  const override = opts.rpeVersionOverride;
  if (override === null || !Number.isSafeInteger(override) || override < 0) return lowered;
  return {
    ...lowered,
    chart: { ...lowered.chart, META: { ...lowered.chart.META, RPEVersion: override, rpeVersionEffective: override } },
  };
}

/** The RPE version override's effect on how Phira plays eased speed (null: nothing changes). */
export function overrideWarning(doc: ChartDoc, opts: ExportOptions): MessageRef | null {
  if (opts.rpeVersionOverride === null || doc.pkg.speedMode !== 'rpe170') return null;
  try {
    return rpeVersionOverrideEffect(doc, opts);
  } catch {
    return null;
  }
}

/** Package switches a bare chart.json cannot carry ("Phira will play this differently without info.yml"). */
export function chartOnlySwitches(
  doc: ChartDoc,
  opts: Pick<ExportOptions, 'target' | 'bakeSpeedForChartOnly'>,
  lowered: Pick<Lowered, 'extra'>,
): MessageRef[] {
  return infoOnlySwitches(doc, exportSpeedMode(doc, opts), lowered.extra !== null);
}

/**
 * The switches of the chart file the project exports alone (the chart-file target and "Copy chart.json"), whatever
 * the chosen export target: the speed bake is the user's preference. The lowering only tells whether extra.json is
 * written, which does not depend on the target; before it is known, the document tells the same (effects, videos or
 * an imported extra.json root, the lowering's own condition).
 */
export function chartFileSwitches(
  doc: ChartDoc,
  bakeSpeed: boolean,
  lowered: Pick<Lowered, 'extra'> | null,
): MessageRef[] {
  const opts = { target: 'chartJson', bakeSpeedForChartOnly: bakeSpeed && doc.pkg.speedMode === 'rpe170' } as const;
  const hasExtra =
    lowered !== null
      ? lowered.extra !== null
      : doc.effects.size > 0 || doc.videos.size > 0 || doc.pkg.extraJsonRoot !== null;
  return infoOnlySwitches(doc, exportSpeedMode(doc, opts), hasExtra);
}

export interface PlannedFile {
  readonly path: string;
  readonly kind: PackageFileEntry['kind'];
  /** Bytes when known (null: not computed). */
  readonly bytes: number | null;
  /** `bytes` is the size this file had in the last export of the project (not computed now). */
  readonly estimated?: boolean;
}

/**
 * Sizes of the files of each project's last export in this session (UX-079): the Export dialog shows them as
 * estimates instead of encoding the chart and phiedit.json on the main thread just to print their sizes.
 */
const lastExportSizes = new Map<string, ReadonlyMap<string, number>>();

/** Remembers the file sizes of an export (called after every successful export). */
export function rememberExportSizes(projectId: ProjectId, files: readonly PackageFileEntry[]): void {
  lastExportSizes.set(projectId, new Map(files.map((f) => [f.path, f.bytes])));
}

/** Fills unknown sizes from the project's last export (marked `estimated`). */
export function withEstimates(projectId: ProjectId, files: readonly PlannedFile[]): PlannedFile[] {
  const last = lastExportSizes.get(projectId);
  if (last === undefined) return [...files];
  return files.map((f) => {
    if (f.bytes !== null) return f;
    const bytes = last.get(f.path);
    return bytes === undefined ? f : { ...f, bytes, estimated: true };
  });
}

/**
 * The files of the export, in the order the exporter writes them, with their sizes. `sizes` false skips encoding
 * the chart and the sidecar (their sizes are then null).
 */
export function plannedFiles(
  doc: ChartDoc,
  idx: DocIndexes,
  opts: ExportOptions,
  lowered: Lowered,
  sizes: boolean,
): PlannedFile[] {
  const plan = planNames(doc, idx, opts);
  const chartSize = sizes ? encodeRpe(lowered.chart, opts).length : null;
  if (opts.target === 'chartJson') return [{ path: plan.chart, kind: 'chart', bytes: chartSize }];
  const out: PlannedFile[] = [];
  const pathOf = (id: ChartDoc['meta']['music']): string => (id === null ? '' : (plan.paths.get(id) ?? ''));
  const info = infoFromDoc(
    doc,
    {
      chart: plan.chart,
      music: pathOf(doc.meta.music),
      illustration: pathOf(doc.meta.illustration),
      unlockVideo: doc.meta.unlockVideo === null ? null : pathOf(doc.meta.unlockVideo),
    },
    exportSpeedMode(doc, opts),
  );
  const enc = new TextEncoder();
  out.push({ path: 'info.yml', kind: 'info.yml', bytes: enc.encode(writeInfoYml(info)).length });
  if (opts.includeInfoTxt && opts.profile === 'rpe-compatible') {
    const txt = writeInfoTxt({
      Name: doc.meta.name,
      Path: doc.meta.rpeId,
      Song: info.music,
      Picture: info.illustration,
      Chart: plan.chart,
      Level: doc.meta.level,
      Composer: doc.meta.composer,
      Charter: doc.meta.charter,
    });
    out.push({ path: 'info.txt', kind: 'info.txt', bytes: enc.encode(txt).length });
  }
  out.push({ path: plan.chart, kind: 'chart', bytes: chartSize });
  if (lowered.extra !== null) {
    out.push({
      path: 'extra.json',
      kind: 'extra.json',
      bytes: sizes ? encodeExtra(lowered.extra, opts.pretty).length : null,
    });
  }
  for (const asset of plan.included) {
    const path = plan.paths.get(asset.id);
    if (path !== undefined) out.push({ path, kind: 'asset', bytes: asset.bytes });
  }
  if (opts.includeSidecar || opts.target === 'backup') {
    out.push({
      path: SIDECAR_FILE,
      kind: 'sidecar',
      bytes: sizes ? Sidecar.write(doc, '0'.repeat(64)).length : null,
    });
  }
  return out;
}

/** Files the chart uses that are missing (they are not written; the checker blocks such an export as PH-01). */
export function missingFiles(doc: ChartDoc, idx: DocIndexes): string[] {
  const out: string[] = [];
  for (const a of doc.assets.values()) {
    if (a.missing !== true) continue;
    const u = idx.assetUsers(a.id);
    if (u.lines.length + u.notes.length + u.effects.length + u.videos.length + u.meta.length > 0) out.push(a.path);
  }
  return out.sort();
}

// ---------------------------------------------------------------------------------------------------------------
// Running the export
// ---------------------------------------------------------------------------------------------------------------

export interface ProducedExport {
  readonly result: ExportResult;
  /** Document sequence number the export was made from. */
  readonly seq: number;
  /** persist.flush() failed: the export used the editor's state, which storage had not confirmed yet. */
  readonly unsaved: boolean;
}

/** flush -> io worker export of the live document (progress 0..1 from the worker's `progress` events). */
export async function produceExport(
  services: Pick<AppServices, 'persist' | 'io'>,
  store: Pick<DocStore, 'projectId' | 'doc' | 'seq'>,
  opts: ExportOptions,
  onProgress?: (p: number) => void,
): Promise<ProducedExport> {
  let unsaved = false;
  try {
    await services.persist.flush();
  } catch {
    unsaved = true;
  }
  const seq = store.seq;
  const off = services.io.on('progress', (e) => {
    if (e.task === 'export') onProgress?.(e.p);
  });
  try {
    // CR-W3-07-5 (UX-079): no main-thread `serializeDoc` + structured clone — the io worker reads the flushed
    // project through its persist reader (`doc` is optional in the contract).
    const result = await services.io.call('exportPackage', { projectId: store.projectId, opts });
    if (result.bytes.length > 0) rememberExportSizes(store.projectId, result.files);
    return { result, seq, unsaved };
  } finally {
    off();
  }
}

/**
 * Why the produced file cannot simply be written: null when it can. 'warned': the file is right but Phira will play
 * it differently from the preview (ExportResult.warnings, e.g. an RPE version override across 170); it needs the
 * same confirmation as a failed self-check, with its own wording.
 */
export function exportVerdict(result: ExportResult): 'empty' | 'selfCheckFailed' | 'warned' | null {
  if (result.bytes.length === 0) return 'empty';
  if (!result.selfCheck.ok) return 'selfCheckFailed';
  return result.warnings.length > 0 ? 'warned' : null;
}

/**
 * After the file was written: the project records the export (one undoable step, so "changed since export"
 * clears) and a version "Exported" is kept. The recorded sequence number is the one the record itself gets when
 * nothing changed during the export, else the one the export was made from (so the later edits count as changes).
 */
export async function recordExport(
  services: Pick<AppServices, 'persist'>,
  store: DocStore,
  done: { readonly result: ExportResult; readonly seq: number; readonly fileName: string; readonly at: number },
): Promise<void> {
  const seq = store.seq === done.seq ? store.seq + 1 : done.seq;
  store.transact(
    { label: { key: m.txnRecordExport, params: { name: done.fileName } }, command: 'file.export' },
    (tx) => {
      Core.doc.recordExport(tx, { at: done.at, seq, sha256: done.result.sha256, fileName: done.fileName });
    },
  );
  try {
    await services.persist.checkpoint(store.projectId, done.fileName, 'export');
  } catch {
    // The file is written; a missing version is not worth failing the export for.
  }
}

/** The toast after a successful export (how to get the file into Phira). */
export function exportedToast(target: ExportTarget, fileName: string, bytes: number, size: string): MessageRef {
  if (target === 'chartJson') return { key: m.toastExportedChart, params: { name: fileName, size } };
  if (target === 'backup') return { key: m.toastExportedBackup, params: { name: fileName, size } };
  return { key: m.toastExported, params: { name: fileName, size, bytes } };
}

// ---------------------------------------------------------------------------------------------------------------
// Saving
// ---------------------------------------------------------------------------------------------------------------

export interface SaveHandle {
  /** The name the file gets (the chosen one in the save dialog). */
  readonly name: string;
  write(bytes: Uint8Array, mime: string): Promise<void>;
  /** The export was abandoned after the destination was chosen: remove what the save dialog created. */
  discard(): Promise<void>;
}

export interface Saver {
  /** The browser can show a save dialog (File System Access, Chromium). */
  readonly canChoose: boolean;
  /** Asks for the destination BEFORE the export runs (keeps the user action); null = cancelled. */
  begin(
    destination: Destination,
    fileName: string,
    target: ExportTarget,
    description: string,
  ): Promise<SaveHandle | null>;
}

interface WritableLike {
  write(data: Uint8Array): Promise<void>;
  close(): Promise<void>;
}

interface FileHandleLike {
  readonly name: string;
  createWritable(): Promise<WritableLike>;
  remove?(): Promise<void>;
}

type SavePicker = (opts: {
  readonly suggestedName: string;
  readonly id: string;
  readonly startIn?: FileHandleLike | 'downloads';
  readonly types: ReadonlyArray<{
    readonly description: string;
    readonly accept: Readonly<Record<string, readonly string[]>>;
  }>;
}) => Promise<FileHandleLike>;

/** Offers bytes as a browser download. */
export function downloadBytes(bytes: Uint8Array, mime: string, fileName: string): void {
  if (typeof URL.createObjectURL !== 'function') return;
  const url = URL.createObjectURL(new Blob([bytes as Uint8Array<ArrayBuffer>], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 30_000);
}

function savePicker(): SavePicker | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { readonly showSaveFilePicker?: SavePicker };
  return typeof w.showSaveFilePicker === 'function' ? w.showSaveFilePicker.bind(window) : null;
}

/** The file chosen last in this session: the save dialog opens in its folder (the browser also remembers it by id). */
let lastHandle: FileHandleLike | null = null;

function isAbort(e: unknown): boolean {
  return typeof e === 'object' && e !== null && (e as { readonly name?: unknown }).name === 'AbortError';
}

/** Downloads, or the browser's save dialog (Chromium) remembering the folder. */
export function browserSaver(): Saver {
  const picker = savePicker();
  return {
    canChoose: picker !== null,
    async begin(destination, fileName, target, description) {
      if (destination === 'saveAs' && picker !== null) {
        const ext = extensionOf(target);
        const accept: Record<string, readonly string[]> =
          target === 'chartJson' ? { 'application/json': ['.json'] } : { 'application/zip': [`.${ext}`, '.zip'] };
        let handle: FileHandleLike;
        try {
          handle = await picker({
            suggestedName: fileName,
            id: 'phiedit-export',
            startIn: lastHandle ?? 'downloads',
            types: [{ description, accept }],
          });
        } catch (e) {
          if (isAbort(e)) return null;
          throw e;
        }
        lastHandle = handle;
        return {
          name: handle.name,
          async write(bytes) {
            const w = await handle.createWritable();
            await w.write(bytes);
            await w.close();
          },
          async discard() {
            try {
              await handle.remove?.();
            } catch {
              // Best effort: an empty file may stay where the user chose to save.
            }
          },
        };
      }
      return {
        name: fileName,
        write(bytes, mime) {
          downloadBytes(bytes, mime, fileName);
          return Promise.resolve();
        },
        discard: () => Promise.resolve(),
      };
    },
  };
}

let activeSaver: Saver | null = null;

/** The saver exports use (the browser's, unless a test installed another). */
export function exportSaver(): Saver {
  activeSaver ??= browserSaver();
  return activeSaver;
}

/** Installs a saver (tests); null restores the browser's. */
export function setExportSaver(saver: Saver | null): void {
  activeSaver = saver;
}

/** Writes text to the system clipboard; false when the browser refuses. */
export async function copyText(text: string): Promise<boolean> {
  try {
    const clip = (navigator as Partial<Navigator>).clipboard;
    if (clip === undefined) return false;
    await clip.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** A clipboard write started while the click still counts as a user action; its text follows later. */
export interface ClipboardSink {
  /** Delivers the text (null: nothing is copied, the started write is cancelled); false when the browser refused. */
  deliver(text: string | null): Promise<boolean>;
}

interface AsyncClipboardLike {
  readonly write?: (items: readonly unknown[]) => Promise<void>;
}

type ClipboardItemCtor = new (items: Readonly<Record<string, Promise<Blob>>>) => unknown;

/**
 * Opens a clipboard write now, during the click: Safari and Firefox accept a clipboard write only while the click
 * still counts as a user action, and the export that produces the text (flush, io worker, self-check) can outlast
 * it. The write gets a ClipboardItem whose text is a promise that `deliver` settles; where the browser has no such
 * write (or refuses it), `deliver` falls back to `writeText`.
 */
export function openClipboard(): ClipboardSink {
  const clip =
    typeof navigator === 'undefined'
      ? undefined
      : ((navigator as Partial<Navigator>).clipboard as AsyncClipboardLike | undefined);
  const Item = (globalThis as { readonly ClipboardItem?: ClipboardItemCtor }).ClipboardItem;
  const blob: { resolve?: (b: Blob) => void; reject?: (e: Error) => void } = {};
  let started: Promise<boolean> | null = null;
  if (typeof clip?.write === 'function' && typeof Item === 'function') {
    try {
      const text = new Promise<Blob>((resolve, reject) => {
        blob.resolve = resolve;
        blob.reject = reject;
      });
      // A cancelled write rejects; nothing waits for it then.
      text.catch(() => undefined);
      started = clip.write([new Item({ 'text/plain': text })]).then(
        () => true,
        () => false,
      );
    } catch {
      started = null;
    }
  }
  let settled = false;
  return {
    async deliver(text) {
      if (settled) return false;
      settled = true;
      if (text === null) {
        blob.reject?.(new Error('nothing to copy'));
        if (started !== null) await started;
        return false;
      }
      if (started !== null && blob.resolve !== undefined) {
        blob.resolve(new Blob([text], { type: 'text/plain' }));
        if (await started) return true;
      }
      return copyText(text);
    },
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Copy chart.json
// ---------------------------------------------------------------------------------------------------------------

export type CopyOutcome =
  | { readonly kind: 'copied'; readonly bytes: number; readonly switches: readonly MessageRef[] }
  | { readonly kind: 'clipboardRefused' }
  | { readonly kind: 'empty'; readonly diffs: readonly string[] }
  | { readonly kind: 'declined' };

/**
 * "Copy chart.json": a chart-only export (strict self-check included) put on the clipboard as text. A failed
 * self-check or an export warning asks `confirmFailed` first; an empty result (the encoder refused the chart) is
 * never copied.
 *
 * Call it synchronously from the click handler: the clipboard write (`sink`, opened by default before anything is
 * awaited) has to start while the click still counts as a user action.
 */
export async function copyChartJson(
  services: Pick<AppServices, 'persist' | 'io'>,
  store: Pick<DocStore, 'projectId' | 'doc' | 'seq'>,
  opts: ExportOptions,
  bakeSpeed: boolean,
  confirmFailed: (diffs: readonly string[], warnings: readonly MessageRef[]) => Promise<boolean>,
  sink: ClipboardSink = openClipboard(),
): Promise<CopyOutcome> {
  const chartOpts: ExportOptions = {
    ...opts,
    target: 'chartJson',
    bakeSpeedForChartOnly: bakeSpeed && store.doc.pkg.speedMode === 'rpe170',
  };
  let produced: ProducedExport;
  try {
    produced = await produceExport(services, store, chartOpts);
  } catch (e) {
    void sink.deliver(null);
    throw e;
  }
  const verdict = exportVerdict(produced.result);
  if (verdict === 'empty') {
    void sink.deliver(null);
    return { kind: 'empty', diffs: produced.result.selfCheck.diffs };
  }
  if (
    (verdict === 'selfCheckFailed' || verdict === 'warned') &&
    !(await confirmFailed(produced.result.selfCheck.diffs, produced.result.warnings))
  ) {
    void sink.deliver(null);
    return { kind: 'declined' };
  }
  const ok = await sink.deliver(new TextDecoder().decode(produced.result.bytes));
  return ok
    ? { kind: 'copied', bytes: produced.result.bytes.length, switches: produced.result.infoOnlySwitches }
    : { kind: 'clipboardRefused' };
}
